/**
 * Pure function: transforms the lean, one-row-per-object summary rows
 * (from /traces/gantt-object-summary) into SVAR Gantt task array.
 * No side effects, no globals, no mutations of inputs.
 *
 * OCEL 2.0 / Gantt two-level model:
 *   Summary row  – one per unique value of summaryAttribute (e.g. one per campaign).
 *                  Start/end auto-derived from the min/max of child task bars.
 *   Task row     – one per object instance (e.g. one per production order).
 *                  Label = value of taskLabelAttribute.
 *                  Bar span = taskStartActivity timestamp → taskEndActivity timestamp.
 *                  Missing activities trigger fallback logic (startFallback / endFallback).
 *
 * Visual flags on each task/summary object:
 *   isStartFallback    – start was estimated from first available event (gray bar)
 *   isEndToday         – end was set to today (light blue/green bar)
 *   isEndFallback      – end was estimated from last event or fixed duration (amber bar)
 *   isEndLastFallback  – end was set to last known event specifically (endFallback='last')
 *
 * @param {Array}  rawData    - flat list of object summary rows (from /traces/gantt-object-summary):
 *                              { object_id, object_type, summary_value, task_label_value,
 *                                start_activity_ts, end_activity_ts, first_event_ts, last_event_ts,
 *                                task_planned_start_value, task_planned_end_value,
 *                                summary_planned_start_value, summary_planned_end_value }
 * @param {Object} settings   - { objectType, summaryAttribute, taskLabelAttribute,
 *                               startFallback, endFallback, endFallbackDays }
 * @param {string} primaryKey - primary key field name (always 'object_id' from API)
 * @param {Date}   [now]      - reference date for "today" fallback (injectable for tests)
 * @returns {Array} SVAR-compatible flat array (summaries before their children)
 */

/** Returns true only when ts is a string/number that parses to a finite Date. */
function isValidTimestamp(ts) {
  if (ts == null) return false;
  const t = new Date(ts).getTime();
  return !isNaN(t);
}

/** Distinct, saturated colors for activity markers — kept clear of the bar
 * fallback colors (gray/amber/green/blue from getTaskColor in GanttChart.jsx)
 * and the planned-date line colors (black/red) so markers read unambiguously
 * against any bar. */
export const MARKER_COLOR_PALETTE = [
  '#7c3aed', // violet
  '#0284c7', // sky
  '#db2777', // rose
  '#059669', // emerald
  '#d97706', // amber-gold
  '#0891b2', // cyan
  '#a21caf', // fuchsia
  '#65a30d', // lime
];

/** Deterministic per-activity color, stable across selection edits (based on
 * activity name, not array position) so a marker's color never changes just
 * because another activity was added/removed from the selection. */
function hashString(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

export function buildMarkerColorMap(markerActivities) {
  const map = {};
  for (const act of markerActivities || []) {
    map[act] = MARKER_COLOR_PALETTE[hashString(act) % MARKER_COLOR_PALETTE.length];
  }
  return map;
}

/**
 * Coerces a value to a finite number when it's already a number, or a string
 * AND the attribute's OCEL-declared type is numeric (some OCEL exports
 * serialize non-integer float attribute values as strings, e.g. "546.3992").
 * Never coerces a genuinely-string attribute just because its value happens
 * to look numeric (e.g. a postal code), which would silently lose
 * information like leading zeros.
 */
function toNumericValue(v, isNumericAttr) {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (isNumericAttr && typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/**
 * Single pass over rows x attrs: for each attribute, true if ANY row's value
 * has a non-negligible fractional part. Non-numeric/NaN/Infinity values are
 * skipped (never force true). Generic over valueAccessor so callers with
 * different row shapes (grid extraValues vs tooltip tooltipValues) share this
 * one loop instead of duplicating it.
 */
export function buildColumnDecimalMap(rows, attrs, valueAccessor, isNumericMap, eps = 1e-9) {
  const map = {};
  for (const attr of attrs) map[attr] = false;
  for (const row of rows) {
    for (const attr of attrs) {
      if (map[attr]) continue;
      const v = toNumericValue(valueAccessor(row, attr), isNumericMap?.[attr]);
      if (v == null) continue;
      if (Math.abs(v - Math.round(v)) > eps) map[attr] = true;
    }
  }
  return map;
}

/**
 * Formats a single attribute cell value: null -> '', non-numeric -> existing
 * String()/JSON.stringify() fallback (never gets a unit suffix), numeric ->
 * integer or capped-decimal string per hasDecimals, then unit suffix appended
 * if provided. Does not mutate value.
 */
export function formatAttributeValue(value, { hasDecimals, unit, isNumericAttr, maxDecimals = 2 } = {}) {
  if (value == null) return '';
  const num = toNumericValue(value, isNumericAttr);
  if (num == null) {
    return typeof value === 'object' ? JSON.stringify(value) : String(value);
  }
  const text = (!hasDecimals || Number.isInteger(num))
    ? String(Math.round(num))
    : String(parseFloat(num.toFixed(maxDecimals)));
  return unit ? `${text} ${unit}` : text;
}

export function transformToGanttData(rawData, settings, primaryKey, now) {
  const today = now ?? new Date();

  const {
    objectType,
    startFallback   = 'first',
    endFallback     = 'today',
    endFallbackDays = 7,
    summaryExtraColumns = [],
    taskExtraColumns    = [],
    taskTooltipAttributes = [],
    markerActivities    = [],
  } = settings;

  const markerColorMap = buildMarkerColorMap(markerActivities);

  const pk = primaryKey;
  if (!pk) return [];

  // Step 1: Build one task bar per object-summary row, applying fallback logic
  const taskBars = [];

  for (const row of rawData) {
    if (row.object_type != null && row.object_type !== objectType) continue;
    const objId = row[pk];
    if (objId == null) continue;

    const hasStart = isValidTimestamp(row.start_activity_ts);
    const hasEnd   = isValidTimestamp(row.end_activity_ts);

    let startDate;
    let endDate;
    let isStartFallback    = false;
    let isEndToday         = false;
    let isEndFallback      = false;
    let isEndLastFallback  = false;

    // ── Determine start date ──────────────────────────────────────────
    if (hasStart) {
      startDate = new Date(row.start_activity_ts);
    } else {
      if (startFallback === 'hide') continue;
      // 'first' — use first available event
      isStartFallback = true;
      startDate = isValidTimestamp(row.first_event_ts)
        ? new Date(row.first_event_ts)
        : today;
    }

    // ── Determine end date ────────────────────────────────────────────
    if (hasEnd) {
      endDate = new Date(row.end_activity_ts);
    } else {
      const lastKnown = isValidTimestamp(row.last_event_ts)
        ? new Date(row.last_event_ts)
        : startDate;

      if (endFallback === 'hide') {
        continue;
      } else if (endFallback === 'today') {
        isEndToday = true;
        endDate = today;
      } else if (endFallback === 'last') {
        isEndFallback = true;
        isEndLastFallback = true;
        endDate = lastKnown;
      } else {
        // 'fixed' — last known event + N days
        isEndFallback = true;
        endDate = new Date(lastKnown.getTime() + endFallbackDays * 24 * 3_600_000);
      }
    }

    if (endDate < startDate) {
      endDate = new Date(startDate.getTime());
    }

    const plannedStart = isValidTimestamp(row.task_planned_start_value)
      ? new Date(row.task_planned_start_value)
      : null;
    const plannedEnd = isValidTimestamp(row.task_planned_end_value)
      ? new Date(row.task_planned_end_value)
      : null;

    taskBars.push({
      summaryKey:              row.summary_value != null ? String(row.summary_value) : '__NO_VALUE__',
      id:                      String(objId),
      text:                    String(row.task_label_value ?? objId),
      start:                   startDate,
      end:                     endDate,
      plannedStart,
      plannedEnd,
      isStartFallback,
      isEndToday,
      isEndFallback,
      isEndLastFallback,
      summaryPlannedStartRaw:  row.summary_planned_start_value ?? null,
      summaryPlannedEndRaw:    row.summary_planned_end_value ?? null,
      taskExtraRaw: Object.fromEntries(
        taskExtraColumns.map(attr => [attr, row[`task_extra__${attr}`] ?? null]),
      ),
      summaryExtraRaw: Object.fromEntries(
        summaryExtraColumns.map(attr => [attr, row[`summary_extra__${attr}`] ?? null]),
      ),
      taskTooltipRaw: Object.fromEntries(
        taskTooltipAttributes.map(attr => [attr, row[`task_tooltip__${attr}`] ?? null]),
      ),
      markerEvents: Array.isArray(row.marker_events)
        ? row.marker_events
            .filter(m => m && isValidTimestamp(m.timestamp))
            .map(m => {
              const activity = String(m.activity ?? '');
              return {
                activity,
                timestamp: new Date(m.timestamp),
                color: markerColorMap[activity] ?? MARKER_COLOR_PALETTE[0],
              };
            })
        : [],
    });
  }

  // Step 2: Group task bars by summaryKey → build summary rows
  const summaryGroups = new Map(); // summaryKey → taskBar[]
  for (const bar of taskBars) {
    if (!summaryGroups.has(bar.summaryKey)) summaryGroups.set(bar.summaryKey, []);
    summaryGroups.get(bar.summaryKey).push(bar);
  }

  // Step 3: Emit summaries (parent: 0) then their children — SVAR requires parents first
  const result = [];
  for (const [summaryKey, bars] of summaryGroups) {
    if (bars.length === 0) continue;

    // Only the child with the max end date is relevant for end-related flags.
    const summaryEndMs  = Math.max(...bars.map(b => b.end.getTime()));
    const summaryEndBar = bars.find(b => b.end.getTime() === summaryEndMs);

    const summaryIsEndToday        = summaryEndBar?.isEndToday        === true;
    const summaryIsEndFallback     = !summaryIsEndToday && (summaryEndBar?.isEndFallback     === true);
    const summaryIsEndLastFallback = !summaryIsEndToday && (summaryEndBar?.isEndLastFallback === true);
    const summaryIsStartFallback   = !summaryIsEndToday && !summaryIsEndFallback
      && bars.every(b => b.isStartFallback);

    const isSummaryFallback = summaryKey === '__NO_VALUE__';
    const summaryId = `summary_${summaryKey}`;

    const summaryStart = new Date(Math.min(...bars.map(b => b.start.getTime())));
    const summaryEnd   = new Date(Math.max(...bars.map(b => b.end.getTime())));

    const summaryPlannedStartRaw = bars.find(b => b.summaryPlannedStartRaw != null)?.summaryPlannedStartRaw;
    const summaryPlannedEndRaw   = bars.find(b => b.summaryPlannedEndRaw   != null)?.summaryPlannedEndRaw;
    const summaryPlannedStartDate = isValidTimestamp(summaryPlannedStartRaw) ? new Date(summaryPlannedStartRaw) : null;
    const summaryPlannedEndDate   = isValidTimestamp(summaryPlannedEndRaw)   ? new Date(summaryPlannedEndRaw)   : null;

    // A summary row aggregates multiple objects — only show a value when every
    // object in the group shares the exact same value (unambiguous). Mixed
    // values across the group would misrepresent the group as a whole, so the
    // cell stays blank instead of showing an arbitrary "first" value.
    const summaryExtraValues = {};
    for (const attr of summaryExtraColumns) {
      const nonNullValues = bars
        .map(b => b.summaryExtraRaw?.[attr])
        .filter(v => v != null);
      const uniqueKeys = new Set(nonNullValues.map(v => (typeof v === 'object' ? JSON.stringify(v) : v)));
      summaryExtraValues[attr] = uniqueKeys.size === 1 ? nonNullValues[0] : null;
    }

    result.push({
      id:               summaryId,
      text:             isSummaryFallback
                          ? `(kein "${settings.summaryAttribute}"-Wert)`
                          : summaryKey,
      type:             'summary',
      parent:           0,
      start:            summaryStart,
      end:              summaryEnd,
      durationH:        Math.round((summaryEnd - summaryStart) / 3_600_000),
      isStartFallback:  summaryIsStartFallback,
      isEndToday:       summaryIsEndToday,
      isEndFallback:    summaryIsEndFallback,
      isEndLastFallback: summaryIsEndLastFallback,
      isSummaryFallback,
      plannedStart:     summaryPlannedStartDate,
      plannedEnd:       summaryPlannedEndDate,
      extraValues:      summaryExtraValues,
    });

    for (const bar of bars) {
      result.push({
        id:               bar.id,
        text:             bar.text,
        type:             'task',
        parent:           summaryId,
        start:            bar.start,
        end:              bar.end,
        durationH:        Math.round((bar.end - bar.start) / 3_600_000),
        isStartFallback:  bar.isStartFallback,
        isEndToday:       bar.isEndToday,
        isEndFallback:    bar.isEndFallback,
        isEndLastFallback: bar.isEndLastFallback,
        plannedStart:     bar.plannedStart,
        plannedEnd:       bar.plannedEnd,
        extraValues:      bar.taskExtraRaw,
        tooltipValues:    bar.taskTooltipRaw,
        markerEvents:     bar.markerEvents,
      });
    }
  }

  return result;
}
